import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { FacilityFormType, InstallationFlowType, TechnicianFormType, UserStatus } from '../constants/app.constants';
import { ApplicationResponseData, KeyValueData, TechnicianData } from '../domains/common.domain';
import { LocalStorageService } from '../services/local-storage.service';
import { NavigationStateShareService } from '../services/navigation-state-share.service';
import { PanelService } from './panel.service';
import { PanelData } from './panel.domain';
import { EnvironmentService } from '../services/environment.service';
import { RegistrationService } from '../registration/registration.service';
import { ErrorService } from '../services/error/error.service';
import { Observable } from 'rxjs';
import { CommonService } from '../services/common.service';
import { SkStatus } from '../+shared/enums/skStatus';
import { ConfirmationService } from 'primeng';
import { ConfirmationModalService } from '../+shared/components/confirmation-modal/confirmation-modal.service';
import { NetzTranslateService } from '../services/netz-translate.service';

@Component({
  providers: [PanelService],
  selector: 'app-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
})
export class PanelComponent implements OnInit {
  allPanels: PanelData[];
  installationFlowType: KeyValueData;
  isCustomer: boolean;
  role: string;
  isApplicationActive: boolean;
  isLoading: boolean;
  technicianData: TechnicianData;
  config: any;

  constructor(
    private route: Router,
    private localStorageService: LocalStorageService,
    private dataShare: NavigationStateShareService,
    private environmentService: EnvironmentService,
    private registrationService: RegistrationService,
    private errorService: ErrorService,
    private commonService: CommonService,
    private confirmationService: ConfirmationService,
    private confirmationModalService: ConfirmationModalService,
    private netzTranslateService: NetzTranslateService
  ) {}

  ngOnInit(): void {
    this.installationFlowType = InstallationFlowType;
    this.role = this.localStorageService.getItem('role');
    this.isCustomer = this.role === UserStatus.CUSTOMER.toLowerCase();
    this.allPanels = this.environmentService.allPanels;
    this.isApplicationActive = this.isCustomer;
    this.isLoading = false;
    this.removeItemsFromStorage();
    if (!this.isCustomer) {
      this.getTechnicianApplication();
    }

    this.config = this.localStorageService.getItem('config');
  }

  redirectTo(flowType: string): void {
    this.localStorageService.setItem('original_flow', flowType);
    const continueToNewApplication = () => {
      this.localStorageService.removeItem('application_id');
      this.localStorageService.removeItem('location_data');
      this.localStorageService.removeItem('area_locations');
      this.continueToApplication(flowType);
    };

    const continueToExistingApplication = applicationId => {
      this.localStorageService.setItem('application_id', applicationId);
      this.continueToApplication(flowType);
    };

    this.getExistingApplicationId(flowType).then(applicationId => {
      if (applicationId) {
        this.confirmationModalService.show({
          message: () => this.netzTranslateService.translateString('basic-profile.jurisdiction.new-application-confirm-text'),
          accept: () => continueToNewApplication(),
          reject: () => {
            continueToExistingApplication(applicationId);
          },
        });
      } else {
        continueToNewApplication();
      }
    });
  }

  private continueToApplication(flowType: string) {
    if (
      (flowType === this.installationFlowType.SiteElectricityMeterdata && this.technicianData?.concession_power === '1') ||
      flowType !== this.installationFlowType.SiteElectricityMeterdata
    ) {
      this.localStorageService.setItem('installation_flow', flowType);
      this.dataShare.setFormState(1);
      this.route.navigate(['/jurisdiction']);
    }
  }

  removeItemsFromStorage(): void {
    this.localStorageService.removeItem('installation_flow');
    this.localStorageService.removeItem('original_flow');
    this.localStorageService.removeItem('selected_facilities');
    this.localStorageService.removeItem('selected_building_type');
    this.localStorageService.removeItem('documents_data');
    this.localStorageService.removeItem('selected_change_type');
    this.localStorageService.removeItem('site_electricity_required');
    this.localStorageService.removeItem('installer_id');
    // this.localStorageService.removeItem('area_locations');
    FacilityFormType.forEach(item => {
      this.localStorageService.removeItem(`${item.key}_facility_data`);
    });
    TechnicianFormType.forEach(item => {
      this.localStorageService.removeItem(`${item.key}_technician_data`);
    });
  }

  showChangeOfHouseConnection(item: PanelData): boolean {
    return (
      (!this.isCustomer || this.environmentService.showChangeHouseConnectionForCustomer) &&
      item.id === 'change-house-connection' &&
      item.display
    );
  }

  showFeedInSystemForCustomer(item: PanelData): boolean {
    return (!this.isCustomer || this.environmentService.showFeedInSystemForCustomer) && item.id === 'feed-in-system' && item.display;
  }

  private getExistingApplicationId(installationFlow: string): Promise<string> {
    return new Promise((resolve, reject) => {
      let applicationSubscription: Observable<any>;
      let flowType: number;
      if (
        installationFlow === this.installationFlowType.NewInstallation ||
        installationFlow === this.installationFlowType.UpdateInstallation ||
        installationFlow === this.installationFlowType.FeedInSystem ||
        installationFlow === this.installationFlowType.SiteElectricityMeterdata
      ) {
        flowType = 1;
        applicationSubscription = this.commonService.getExistingInstallationApplications(installationFlow);
      } else if (
        installationFlow === this.installationFlowType.MainsConnection ||
        installationFlow === this.installationFlowType.TemporaryConnection ||
        installationFlow === this.installationFlowType.ChangeHouseConnection ||
        installationFlow === this.installationFlowType.ConversionToGas
      ) {
        flowType = 2;
        applicationSubscription = this.commonService.getExistingGridConnectionApplication(installationFlow);
      } else if (installationFlow === this.installationFlowType.GridCompatibility) {
        flowType = 3;
        applicationSubscription = this.commonService.getExistingGridCompFeedInApplications(installationFlow);
      } else if (installationFlow === this.installationFlowType.RegEP) {
        flowType = 4;
        applicationSubscription = this.commonService.getExistingRegepApplications(SkStatus.created);
      }

      this.isLoading = true;
      applicationSubscription.subscribe(
        (response: any) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          /*
          (response as GetInstallationApplicationObject).installationapplication &&
            (response as GetInstallationApplicationObject).installationapplication.length) ||
            (response[0] as GridCompFeedInApplicationData)?.technical_data ||
            (response[0] as RegistrationOfEnergyProducerApplication)?.technical_data ||
            ((response as GetGridConnectionObject).gridconapplication && (response as GetGridConnectionObject).gridconapplication.length))
          */

          const existingApplication =
            (response.installationapplication && response.installationapplication[0]) ??
            response[0] ??
            response.technical_data ??
            (response.gridconapplication && response.gridconapplication[0]);

          return resolve(existingApplication?.application_id);
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    });
  }

  private getTechnicianApplication() {
    const technicianId = this.localStorageService.getItem('technician_id');

    this.isLoading = true;
    this.registrationService.getTechnicianApplication(technicianId, 'technician_id').subscribe(
      (response: ApplicationResponseData) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.isApplicationActive =
          response?.technician_application?.length &&
          [UserStatus.GRANTED.toLowerCase(), UserStatus.ISSUED.toLowerCase(), UserStatus.PARTIALLY_ISSUED.toLowerCase()].includes(
            response.technician_application[0].status?.toLowerCase()
          );
        this.technicianData = response.technician_application[0]?.technician[0];
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }
}
